import { useState, useMemo, useEffect } from "react";
import { countries } from "countries-list";
import { useTheme } from "@mui/material";
import { GeographicRegion } from "../../../api/calendarEvent";

export interface CountryDropdownProps {
  initialCountry: GeographicRegion | null;
  onSelect: (country: GeographicRegion | null) => void;
}

const useCountryDropdown = ({
  initialCountry,
  onSelect,
}: CountryDropdownProps) => {
  const { spacing } = useTheme();
  const [searchValue, setSearchValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [selectedCountry, setSelectedCountry] =
    useState<GeographicRegion | null>(null);

  const handleOptionSelect = (
    _event: unknown,
    newValue: GeographicRegion | null,
  ) => {
    setSelectedCountry(newValue);
    onSelect(newValue);
  };

  const allCountries: GeographicRegion[] = useMemo(() => {
    return Object.entries(countries).map(([code, data]) => ({
      code,
      name: data.name,
    }));
  }, []);

  const filteredCountries = useMemo(() => {
    if (!searchValue) return allCountries;
    const searchLower = searchValue.toLowerCase();
    return allCountries.filter((country) =>
      country.name.toLowerCase().includes(searchLower),
    );
  }, [searchValue, allCountries]);

  const handleInputChange = (_event: unknown, newValue: string) => {
    setIsLoading(true);
    setSearchValue(newValue);
    setIsLoading(false);
  };

  useEffect(() => {
    if (initialCountry) {
      const found = filteredCountries.find(
        (opt) => opt.code === initialCountry.code,
      );
      setSelectedCountry(found || null);
    }
  }, [initialCountry, filteredCountries]);

  return {
    options: filteredCountries,
    handleInputChange,
    isLoading,
    spacing,
    selectedCountry,
    handleOptionSelect,
  };
};

export default useCountryDropdown;
